import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TestReportResultDTO } from "../../services/TestReport/TestReport.service";

export type ToleranceMap = Map<number, { min: number | null; max: number | null }>;

export interface PdfMeta {
  reportId: number | string;
  reportDate?: Date | string | null;
  manufacturingUnitName?: string | null;
  testedDate?: Date | string | null;
  analysisStart?: Date | string | null;
  analysisEnd?: Date | string | null;
  sampleQty?: number | string | null;
  // Optional helpers if you later want to pass them from service:
  sampleQtyUnit?: string | null;   // e.g., "ml"
  sampleQtyText?: string | null;   // e.g., "250 ml"
  testerName?: string | null;
  oilName?: string | null;
  batchName?: string | null;
  companyLines?: string[];
}

const BRAND_GREEN: [number, number, number] = [0, 102, 51];
const LIGHT_GREY: [number, number, number] = [245, 245, 245];
const WATERMARK_OPACITY = 0.16;

// Oil strip gradient (very light mint -> light green)
const OIL_STRIP_FROM: [number, number, number] = [232, 248, 240];
const OIL_STRIP_TO: [number, number, number] = [204, 236, 221];

const FSSAI_CODE_BY_NAME: Record<string, string> = {
  "ACID VALUE": "FSSAI 02.009:2021",
  "IODINE VALUE": "FSSAI 02.010:2021",
  "MINERAL OIL PRESENCE TEST": "FSSAI 02.029:2021",
  "MOISTURE TEST": "FSSAI 02.001:2021",
  "POLENSKE VALUE": "FSSAI 02.011:2021",
  "REFRACTIVE INDEX": "FSSAI 02.003:2021",
  "SAPONIFICATION VALUE": "FSSAI 02.007:2021",
  "UNSAPONIFICATION VALUE": "FSSAI 02.008:2021",
  "AREGEMONE TEST": "FSSAI 02.032:2021",
  "BUTYRO REFRACTOMETER": "FSSAI 02.003:2021",
};

/* =========================
   Address presets by MU
   ========================= */
const MU_POS003_LINES = [
  "Behind Govt School,",
  "Adaganahalli Village,",
  "K R Nagar Taluk,",
  "Mysuru District,",
  "Karnataka – 571602,",
  "India",
];

const MU_POS004_LINES = [
  "Rajanapalya,",
  "Magadi Taluk,",
  "Ramanagara District,",
  "Karnataka – 562120,",
  "India",
];

const norm = (s: string) => s.replace(/[^a-z0-9]/gi, "").toUpperCase();

function resolveCompanyLines(meta: PdfMeta): string[] {
  const raw = Array.isArray(meta.companyLines) ? meta.companyLines : [];
  const fromMeta = raw
    .flatMap((line) => String(line ?? "").split(","))
    .map((s) => s.trim())
    .filter(Boolean);

  if (fromMeta.length > 0) return fromMeta;

  const n = norm((meta.manufacturingUnitName ?? "").toString());
  if (n.includes("UNIT1KR") || n.includes("KRNAGAR")) return MU_POS003_LINES;
  if (n.includes("UNIT2MT") || n.includes("MAGADI")) return MU_POS004_LINES;
  return [];
}

const fmtDate = (d?: Date | string | null) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
const fmtTimeOnly = (d?: Date | string | null) => (d ? new Date(d).toLocaleTimeString() : "—");
const fmt4 = (n: number) => Number(n).toFixed(4);
const normalizeStatus = (s?: string | null) => (s ?? "—").trim().toUpperCase() || "—";

const loadImageAsDataURL = (src?: string): Promise<string> =>
  new Promise((resolve) => {
    if (!src) return resolve("");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext("2d");
      if (ctx) ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve("");
    img.src = src;
  });

function gradientRect(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  from: [number, number, number],
  to: [number, number, number],
  orientation: "vertical" | "horizontal" = "vertical",
  steps = 90
) {
  const [r1, g1, b1] = from;
  const [r2, g2, b2] = to;

  if (orientation === "vertical") {
    const stepH = h / steps;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      doc.setFillColor(r, g, b);
      doc.rect(x, y + i * stepH, w, stepH + 0.5, "F");
    }
  } else {
    const stepW = w / steps;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      doc.setFillColor(r, g, b);
      doc.rect(x + i * stepW, y, stepW + 0.5, h, "F");
    }
  }
}

type Sizing = {
  M: number;
  titleSize: number;
  logoW: number;
  logoH: number;
  addrFont: number;
  addrLineGap: number;
  metaFont: number;
  metaLineGap: number;
  oilRowFont: number;
  oilRowPad: number;
  resultsFont: number;
  resultsPad: number;
  bottomReserve: number;
};

// Format Sample Quantity with unit "ml" (unless already has a unit/string)
const fmtSampleQtyDisplay = (meta: PdfMeta): string => {
  // If caller provided a ready text, honor it
  if (meta.sampleQtyText != null && String(meta.sampleQtyText).trim() !== "") {
    return String(meta.sampleQtyText).trim();
  }

  const v = meta.sampleQty;
  if (v == null) return "—";
  const raw = String(v).trim();
  if (raw === "") return "—";

  // If it already has alphabetic characters (likely a unit), use as-is
  if (/[a-zA-Z]/.test(raw)) return raw;

  // Otherwise treat as numeric and append unit (default "ml")
  const unit = (meta.sampleQtyUnit ?? "ml").toString().trim();
  return `${raw} ${unit}`;
};

function renderAttempt(opts: {
  sizing: Sizing;
  centerLogoDataUrl?: string;
  qaSealDataUrl?: string;
  meta: PdfMeta;
  results: TestReportResultDTO[];
  tolerances: ToleranceMap;
  methodByTestId: Record<number, string>;
}) {
  const {
    sizing: {
      M,
      titleSize,
      logoW,
      logoH,
      addrFont,
      addrLineGap,
      metaFont,
      metaLineGap,
      resultsFont,
      resultsPad,
      bottomReserve,
    },
    centerLogoDataUrl,
    qaSealDataUrl,
    meta,
    results,
    tolerances,
    methodByTestId,
  } = opts;

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const CONTENT_L = M + 18;
  const CONTENT_R = pageW - (M + 18);
  const CONTENT_W = CONTENT_R - CONTENT_L;

  // Border
  doc.setDrawColor(180).setLineWidth(0.8);
  doc.rect(M, M, pageW - 2 * M, pageH - 2 * M);

  /* HEADER (white) */
  const HEADER_H = 85;
  const headerY = M;

  // Logo
  const LOGO_MAX_W = 130,
    LOGO_MAX_H = 50;
  if (centerLogoDataUrl) {
    const w = Math.min(LOGO_MAX_W, logoW);
    const h = Math.min(LOGO_MAX_H, logoH);
    doc.addImage(centerLogoDataUrl, "PNG", CONTENT_L, headerY + (HEADER_H - h) / 2, w, h);
  }

  // Title
  doc.setFont("helvetica", "bold").setTextColor(0).setFontSize(titleSize + 3);
  doc.text("TEST REPORT", pageW / 2, headerY + HEADER_H / 2 + 6, { align: "center" });

  // QA Seal (top-right)
  if (qaSealDataUrl) {
    const QA_SEAL_W = 90;
    const QA_SEAL_H = 75;
    const sealX = CONTENT_R - QA_SEAL_W;
    doc.addImage(qaSealDataUrl, "PNG", sealX, headerY + 4, QA_SEAL_W, QA_SEAL_H);
  }

  /* META AREA */
  const metaTop = headerY + HEADER_H + 40; // controls gap below header
  const COL_GAP = 14;

  const LEFT_W = Math.min(CONTENT_W * 0.4, 180);
  const RIGHT_W = CONTENT_W - LEFT_W - COL_GAP;
  const rightX = CONTENT_L + LEFT_W + COL_GAP;

  // Address
  const companyLines = resolveCompanyLines(meta);
  doc.setFontSize(addrFont + 0.5).setFont("helvetica", "bold").setTextColor(25);
  doc.text("Address:", CONTENT_L, metaTop);
  doc.setFont("helvetica", "normal").setTextColor(55);
  let yL = metaTop + 12;
  for (const ln of companyLines) {
    doc.text(ln, CONTENT_L, yL, { maxWidth: LEFT_W });
    yL += addrLineGap;
  }
  const leftBlockBottom = yL;

  // Right 2×4 grid
  const innerGap = 30;
  const innerColW = (RIGHT_W - innerGap) / 2;
  const col1: Array<[string, string]> = [
    ["Report No", String(meta.reportId ?? "—")],
    ["Report Date", fmtDate(meta.reportDate)],
    ["Analysis Start", fmtTimeOnly(meta.analysisStart)],
    ["Analysis End", fmtTimeOnly(meta.analysisEnd)],
  ];
  const col2: Array<[string, string]> = [
    ["Manufacturing Unit", String(meta.manufacturingUnitName ?? "—")],
    ["Tested By", meta.testerName ?? "—"],
    ["Tested Date", fmtDate(meta.testedDate)],
    ["Sample Qty", fmtSampleQtyDisplay(meta)],
  ];

  doc.setFontSize(metaFont);
  const LABEL_PAD = 8;
  const MAX_LW1 = 110;
  const MAX_LW2 = 100;

  let yRow = metaTop;
  for (let i = 0; i < 4; i++) {
    const [k1, v1] = col1[i];
    const k1Text = `${k1}:`;
    const k1Width = Math.min(MAX_LW1, doc.getTextWidth(k1Text) + LABEL_PAD);
    doc.setFont("helvetica", "bold").setTextColor(30);
    doc.text(k1Text, rightX, yRow);
    doc.setFont("helvetica", "normal").setTextColor(70);
    doc.text(v1, rightX + k1Width, yRow, { maxWidth: innerColW - k1Width });

    const [k2, v2] = col2[i];
    const x2 = rightX + innerColW + innerGap;
    const k2Text = `${k2}:`;
    const k2Width = Math.min(MAX_LW2, doc.getTextWidth(k2Text) + LABEL_PAD);
    doc.setFont("helvetica", "bold").setTextColor(30);
    doc.text(k2Text, x2, yRow);
    doc.setFont("helvetica", "normal").setTextColor(70);
    doc.text(v2, x2 + k2Width, yRow, { maxWidth: innerColW - k2Width });

    yRow += metaLineGap;
  }

  const metaBottom = Math.max(leftBlockBottom, yRow) + 6;

  /* OIL TYPE / BATCH STRIP (light green between lines) */
  const bandY = metaBottom + 8;
  const bandH = 26;

  // lines
  doc.setDrawColor(150).setLineWidth(0.8);
  doc.line(CONTENT_L, bandY, CONTENT_R, bandY);
  doc.line(CONTENT_L, bandY + bandH, CONTENT_R, bandY + bandH);

  // gradient strip
  const STRIP_INSET = 1.2;
  gradientRect(
    doc,
    CONTENT_L,
    bandY + STRIP_INSET,
    CONTENT_W,
    bandH - STRIP_INSET * 2,
    OIL_STRIP_FROM,
    OIL_STRIP_TO,
    "horizontal",
    70
  );

  // text
  const LABEL_COLOR: [number, number, number] = [0, 90, 45];
  const VALUE_COLOR: [number, number, number] = [0, 0, 0];
  const leftPad = 10;
  const centerPad = 155;
  const oilLabel = "Oil Type : ";
  const batchLabel = "Batch No : ";
  const oilVal = String(meta.oilName ?? "—");
  const batchVal = String(meta.batchName ?? "—");
  const textY = bandY + bandH / 2 + 3;

  doc.setFontSize(10.5).setFont("helvetica", "bold").setTextColor(...LABEL_COLOR);
  const labelW = doc.getTextWidth(oilLabel);
  doc.text(oilLabel, CONTENT_L + leftPad, textY);

  doc.setFont("helvetica", "normal").setTextColor(...VALUE_COLOR);
  const maxOilW =
    CONTENT_W - leftPad - labelW - centerPad - doc.getTextWidth(batchLabel + batchVal) - 6;

  let oilText = oilVal;
  if (doc.getTextWidth(oilText) > maxOilW && maxOilW > 20) {
    while (oilText.length > 1 && doc.getTextWidth(oilText + "…") > maxOilW) {
      oilText = oilText.slice(0, -1);
    }
    oilText += "…";
  }
  doc.text(oilText, CONTENT_L + leftPad + labelW, textY);

  doc.setFont("helvetica", "bold").setTextColor(...LABEL_COLOR);
  const batchX = CONTENT_L + leftPad + labelW + doc.getTextWidth(oilText) + centerPad;
  doc.text(batchLabel, batchX, textY);
  doc.setFont("helvetica", "normal").setTextColor(...VALUE_COLOR);
  doc.text(batchVal, batchX + doc.getTextWidth(batchLabel), textY);

  /* RESULTS TABLE (BIGGER LOOK) */
  const tableStartY = bandY + bandH + 8;
  const head = [["Sl.No", "Test", "Method No", "Result", "FSSAI Tolerance", "Status"]];
  const usableW = CONTENT_W;

  const getRangeText = (testId: number) => {
    const tol = tolerances.get(testId);
    if (!tol) return "—";
    const { min, max } = tol;
    if (min != null && max != null) return `${fmt4(min)} – ${fmt4(max)}`;
    if (min != null) return `≥ ${fmt4(min)}`;
    if (max != null) return `≤ ${fmt4(max)}`;
    return "—";
  };

  const body = (results ?? []).map((r, i) => {
    const resultText = r.result != null ? fmt4(r.result) : "—";
    const method =
      (methodByTestId?.[r.testId] ??
        FSSAI_CODE_BY_NAME[(r.testName ?? "").trim().toUpperCase()]) || "—";

    const isAlmondUnsap =
      (meta.oilName ?? "").toUpperCase().includes("ALMOND") &&
      (r.testName ?? "").trim().toUpperCase() === "UNSAPONIFICATION VALUE";

    const rangeText = isAlmondUnsap ? "N.S." : getRangeText(r.testId);
    const statusText = isAlmondUnsap ? "" : normalizeStatus(r.status);

    return [String(i + 1), r.testName || `Test ${r.testId}`, method, resultText, rangeText, statusText];
  });

  autoTable(doc, {
    startY: tableStartY,
    head,
    body,
    theme: "grid",

    styles: {
      fontSize: resultsFont,
      cellPadding: resultsPad,
      valign: "middle",
      minCellHeight: 22,
    },
    headStyles: {
      fillColor: BRAND_GREEN,
      textColor: 255,
      fontStyle: "bold",
      fontSize: resultsFont + 2,
      cellPadding: resultsPad + 1,
    },
    bodyStyles: {
      cellPadding: resultsPad,
    },
    alternateRowStyles: { fillColor: LIGHT_GREY },

    margin: { left: CONTENT_L, right: pageW - CONTENT_R, bottom: bottomReserve },

    columnStyles: {
      0: { cellWidth: 0.08 * usableW, halign: "center" },
      1: { cellWidth: 0.32 * usableW }, // Test Name column
      2: { cellWidth: 0.18 * usableW },
      3: { cellWidth: 0.14 * usableW, halign: "right" },
      4: { cellWidth: 0.18 * usableW, halign: "center" },
      5: { cellWidth: 0.10 * usableW, halign: "center" },
    },

    didParseCell: (data: any) => {
      if (data.section === "body" && data.column.index === 1) {
        // Make Test Name bold
        data.cell.styles.fontStyle = "bold";
      }
    },

    didDrawCell: (data: any) => {
      // Status pill logic
      if (data.section === "body" && data.column.index === 5) {
        const val = String(data.cell.raw || "").toUpperCase();
        if (!val) return;
        const { x, y, width, height } = data.cell;

        const pillPadX = 8;
        const pillW = Math.min(width - 6, doc.getTextWidth(val) + pillPadX * 2);
        const pillH = Math.min(height - 6, resultsFont + 10);
        const px = x + (width - pillW) / 2;
        const py = y + (height - pillH) / 2;

        if (val === "PASS") doc.setFillColor(220, 245, 225);
        else if (val === "FAIL") doc.setFillColor(253, 225, 225);
        else doc.setFillColor(235, 235, 235);

        (doc as any).roundedRect(px, py, pillW, pillH, 7, 7, "F");
        doc.setFont("helvetica", "bold");
        if (val === "FAIL") doc.setTextColor(170, 0, 0);
        else doc.setTextColor(0, 120, 0);
        doc.text(val, px + pillW / 2, py + pillH / 2 + 3, { align: "center" });
        doc.setTextColor(0);
      }
    },

    // --- Re-added didDrawPage to render watermark + footer + signature lines per page ---
    didDrawPage: (data: any) => {
      // Watermark (center logo) with opacity
      try {
        if (centerLogoDataUrl && (doc as any).GState && (doc as any).setGState) {
          const gs = new (doc as any).GState({ opacity: WATERMARK_OPACITY * 0.8 });
          (doc as any).setGState(gs);
          const wmW = CONTENT_W * 0.75;
          const wmH = wmW * 0.55;
          const wmX = CONTENT_L + (CONTENT_W - wmW) / 2;
          const wmY = M + 300;
          doc.addImage(centerLogoDataUrl, "PNG", wmX, wmY, wmW, wmH);
          (doc as any).setGState(new (doc as any).GState({ opacity: 1 }));
        }
      } catch (err) {
        // ignore if GState unsupported in runtime
      }

      // Footer area (signature lines and note)
      const footerBottom = pageH - M;
      const SIG_LINE_FROM_BOTTOM = 38;
      const SIG_LABEL_OFFSET = 12;
      const SIG_LINE_W = 220;

      const sigLineY = footerBottom - SIG_LINE_FROM_BOTTOM;
      const leftX = CONTENT_L + 18;
      const rightX = CONTENT_R - 18 - SIG_LINE_W;

      doc.setDrawColor(90).setLineWidth(0.8);
      doc.line(leftX, sigLineY, leftX + SIG_LINE_W, sigLineY);
      doc.line(rightX, sigLineY, rightX + SIG_LINE_W, sigLineY);

      doc.setFontSize(10).setTextColor(0);
      doc.text("Verified by", leftX, sigLineY + SIG_LABEL_OFFSET);
      doc.text("Authorised Signatory", rightX, sigLineY + SIG_LABEL_OFFSET);

      // Note box
      const NOTE_H = 22;
      const noteTop = sigLineY - 10 - NOTE_H;
      doc.setFillColor(245, 245, 245);
      doc.rect(CONTENT_L, noteTop, CONTENT_W, NOTE_H, "F");
      doc.setFontSize(9).setTextColor(10);
      doc.text(
        "Note: The given sample conforms to FSSAI specification for the above tested parameters.",
        CONTENT_L + 6,
        noteTop + NOTE_H - 7,
        { maxWidth: CONTENT_W - 12 }
      );

      // Page numbering and "Generated on"
      const pageNo = (doc as any).getCurrentPageInfo ? (doc as any).getCurrentPageInfo().pageNumber : doc.getNumberOfPages();
      const total = doc.getNumberOfPages();
      doc.setFontSize(9).setTextColor(120);
      doc.text(`Page ${pageNo} of ${total}`, CONTENT_R, footerBottom - 6, { align: "right" });
      doc.text(`Generated on ${new Date().toLocaleString("en-GB")}`, CONTENT_L, footerBottom - 6);
    },
  });

  return doc;
}

/** Build the PDF and return the jsPDF instance (no download). */
export async function buildTestReportPdf(args: {
  meta: PdfMeta;
  results: TestReportResultDTO[];
  tolerances: ToleranceMap;
  methodByTestId?: Record<number, string>;
  centerLogoUrl?: string;
  qaSealUrl?: string;
  filename?: string; // ignored here, used by generate*
}): Promise<jsPDF> {
  const { meta, results, tolerances, methodByTestId = {}, centerLogoUrl, qaSealUrl } = args;

  const logoData = centerLogoUrl ? await loadImageAsDataURL(centerLogoUrl) : "";
  const sealData = qaSealUrl ? await loadImageAsDataURL(qaSealUrl) : "";

  // Big table defaults
  const attempts: Sizing[] = [
    {
      M: 18,
      titleSize: 18,
      logoW: 180,
      logoH: 60,
      addrFont: 9,
      addrLineGap: 12,
      metaFont: 9,
      metaLineGap: 12,
      oilRowFont: 9,
      oilRowPad: 4,
      resultsFont: 6,
      resultsPad: 6,
      bottomReserve: 84,
    },
    {
      M: 16,
      titleSize: 17,
      logoW: 160,
      logoH: 54,
      addrFont: 9,
      addrLineGap: 11,
      metaFont: 9,
      metaLineGap: 11,
      oilRowFont: 9,
      oilRowPad: 3,
      resultsFont: 6.5,
      resultsPad: 5.5,
      bottomReserve: 82,
    },
    {
      M: 14,
      titleSize: 16,
      logoW: 150,
      logoH: 50,
      addrFont: 8.5,
      addrLineGap: 10,
      metaFont: 8.5,
      metaLineGap: 10,
      oilRowFont: 8.5,
      oilRowPad: 2,
      resultsFont: 6,
      resultsPad: 5,
      bottomReserve: 78,
    },
  ];

  let finalDoc: jsPDF | null = null;
  for (const sizing of attempts) {
    const doc = renderAttempt({
      sizing,
      centerLogoDataUrl: logoData,
      qaSealDataUrl: sealData,
      meta,
      results,
      tolerances,
      methodByTestId,
    });
    if (doc.getNumberOfPages() <= 1) {
      finalDoc = doc;
      break;
    }
    finalDoc = doc;
  }

  return finalDoc ?? new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
}

/** Build and immediately download the PDF (wrapper). */
export async function generateTestReportPdf(args: {
  meta: PdfMeta;
  results: TestReportResultDTO[];
  tolerances: ToleranceMap;
  methodByTestId?: Record<number, string>;
  centerLogoUrl?: string;
  qaSealUrl?: string;
  filename?: string;
}) {
  const { filename } = args;
  const doc = await buildTestReportPdf(args);
  doc.save(filename ?? `TestReport_${args.meta.reportId}.pdf`);
}

export type { PdfMeta as TestReportPdfMeta };